/**
 * Create an empty dataset, then go straight to uploading its first CSV. On the
 * Datasets page it also offers "Pull from traces" (a live dataset), which
 * hands over to that dialog with the name kept.
 */

import { useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Field, Option } from "@/components/ui/field";
import { Dialog } from "@/components/ui/overlay";
import { errorText } from "@/components/ui/states";
import { useUiStore } from "@/stores/ui-store";
import { useCreateDataset } from "./hooks";

/**
 * A dataset belongs to no agent. `agentId` (from "Create a dataset" in the
 * new-experiment flow) only carries on to the upload, so its columns are
 * matched to that agent's inputs; `returnTo` is where the upload goes back to
 * once saved, with the new dataset picked. `onPullFromTraces`, when given,
 * adds the choice of a live dataset.
 */
export function NewDatasetDialog({
  agentId,
  returnTo,
  onPullFromTraces,
  onClose,
}: {
  agentId: string | null;
  returnTo?: string | undefined;
  onPullFromTraces?: (name: string) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState("");
  const [from, setFrom] = useState<"csv" | "traces">("csv");
  const create = useCreateDataset();
  const notify = useUiStore((s) => s.notify);
  const navigate = useNavigate();

  const submit = () =>
    create.mutate(
      { name: name.trim() },
      {
        onSuccess: (d) => {
          const query = new URLSearchParams({
            ...(agentId ? { agent: agentId } : {}),
            ...(returnTo ? { returnTo } : {}),
          }).toString();
          navigate(`/datasets/${d.id}/import${query ? `?${query}` : ""}`);
        },
        onError: (e) => notify(errorText(e, "Could not create the dataset."), "error"),
      },
    );

  return (
    <Dialog
      title="New dataset"
      subtitle={
        returnTo
          ? "Create it and add items from a CSV file. You come back here once it is saved."
          : onPullFromTraces
            ? "Fill it from a CSV file, or from your agent's traces in Langfuse or runs in qFlow."
            : "Create it, then add items from a CSV file."
      }
      onClose={onClose}
      width={520}
      footer={
        <>
          <span className="flex-1" />
          <Button onClick={onClose}>Cancel</Button>
          {from === "traces" && onPullFromTraces ? (
            <Button variant="primary" disabled={!name.trim()} onClick={() => onPullFromTraces(name.trim())}>
              Next: choose traces
            </Button>
          ) : (
            <Button variant="primary" disabled={!name.trim() || create.isPending} onClick={submit}>
              Create and upload CSV
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Name" required htmlFor="ds-name">
          <input
            id="ds-name"
            className="inp"
            value={name}
            placeholder="e.g. Case inclusion · CT chest"
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        {onPullFromTraces && (
          <fieldset className="flex flex-col gap-2.5">
            <legend className="mb-2 text-[13px] font-semibold">Where do the items come from?</legend>
            <Option
              type="radio"
              name="ds-from"
              checked={from === "csv"}
              onChange={() => setFrom("csv")}
              note="Inputs and expected outputs you prepared. Each upload saves a new version."
            >
              Upload CSV
            </Option>
            <Option
              type="radio"
              name="ds-from"
              checked={from === "traces"}
              onChange={() => setFrom("traces")}
              note="Production traces from Langfuse, or workflow runs from qFlow. They are pulled when an experiment runs on it."
            >
              Pull from traces
            </Option>
          </fieldset>
        )}
      </div>
    </Dialog>
  );
}
